import { NextLayoutComponentType, NextPageContext } from 'next';
import Router from 'next/router';
import { useEffect } from 'react';

const isBrowser = () => {
  return typeof window !== 'undefined';
};

interface Props<T> {
  Component: NextLayoutComponentType<T>;
  clientCondition?: () => boolean;
  serverCondition?: (e: NextPageContext) => boolean | Promise<boolean>;
  location?: (ctx?: NextPageContext) => string;
}

/**
 * Support conditional redirecting, both server-side and client-side.
 *
 * Client-side, we can use next/router. But that doesn't exist on the server.
 * So on the server we must do an HTTP redirect. This component handles
 * the logic to detect whether on the server and client and redirect
 * appropriately.
 *
 * @param Component The component that this functionality
 * will be added to.
 * @param clientCondition A function that returns a boolean representing
 * whether to perform the redirect. It will always be called, even on
 * the server. This is necessary so that it can have hooks in it (since
 * can't be inside conditionals and must always be called).
 * @param serverCondition A function that returns a boolean representing
 * whether to perform the redirect. It is only called on the server. It
 * accepts a Next page context as a parameter so that the request can
 * be examined and the response can be changed.
 * @param location The location to redirect to.
 */
export default function withConditionalRedirect<T extends object>({
  Component,
  clientCondition = () => false,
  serverCondition = () => false,
  location,
}: Props<T>): NextLayoutComponentType<T> {
  const WithConditionalRedirectWrapper: NextLayoutComponentType<T> = (props) => {
    useEffect(() => {
      if (isBrowser() && clientCondition?.()) {
        if (Router.pathname !== '404') {
          Router.push(location?.() ?? '/404').then();
        }
      }
    }, []);
    return <Component {...props} />;
  };
  WithConditionalRedirectWrapper.getInitialProps = async (ctx: NextPageContext) => {
    location?.(ctx);
    if (!isBrowser()) {
      if (await serverCondition?.(ctx)) {
        if (ctx.res) {
          if (ctx.pathname !== '/404') {
            let to = ctx.pathname === (location?.(ctx) ?? '/404') ? '/404' : location?.(ctx);
            ctx.res.writeHead(303, { Location: to });
          }
          ctx.res.end();
        }
      }
    }
    const componentProps = Component.getInitialProps && (await Component.getInitialProps(ctx));
    return { ...componentProps };
  };
  WithConditionalRedirectWrapper.getLayout = Component.getLayout;
  // WithConditionalRedirectWrapper.getInitialProps = Component.getInitialProps;
  return WithConditionalRedirectWrapper;
}
